import { Component, EventEmitter, HostListener, Input, Output, OnInit, OnDestroy, signal } from '@angular/core';
import { FileResponse } from '../services/api.service';
import { Observable, Subscription } from 'rxjs';
import { FileService } from '../services/file.service';

@Component({
  selector: 'cert-document-viewer',
  templateUrl: './document-viewer.component.html',
  styles: ``,
  standalone: false,
})
export class DocumentViewerComponent implements OnInit, OnDestroy {
  @Input() document$!: Observable<FileResponse>;
  @Output() nextDocument = new EventEmitter();
  @Output() previousDocument = new EventEmitter();

  constructor(private readonly fileService: FileService) {}

  pageNum = 1;
  document!: FileResponse;
  pdfBlob = signal<Blob | undefined>(undefined);
  pdfDataUrl!: string;
  documentAngle = signal<0 | 90 | 180 | 270>(0);

  zoomLevel = signal<number | string>(80);
  private zoomMin = 10;
  private zoomMax = 160;
  private docSub?: Subscription;

  ngOnInit() {
    this.docSub = this.document$.subscribe((x) => {
      this.replacePdf(x);
    });
  }

  ngOnDestroy() {
    this.cleanupPdfUrl();
    this.docSub?.unsubscribe();
  }

  private replacePdf(file: FileResponse) {
    this.cleanupPdfUrl();
    this.document = file;
    const blob = new Blob([file.data], { type: 'application/pdf' });
    this.pdfBlob.set(blob);
    this.pdfDataUrl = URL.createObjectURL(blob);
    this.pageNum = 1;
    this.documentAngle.set(0);
  }

  private cleanupPdfUrl() {
    if (this.pdfDataUrl) {
      URL.revokeObjectURL(this.pdfDataUrl);
    }
  }

  private clearViewer() {
    this.cleanupPdfUrl();
    this.pdfDataUrl = '';
    this.pdfBlob.set(undefined);
    this.pageNum = 1;
    this.documentAngle.set(0);
  }

  onViewDocumentInNewTab() {
    if (this.pdfDataUrl) {
      this.fileService.openPdfURLInNewTab(this.pdfDataUrl);
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') {
      this.onPreviousDocument();
    } else if (event.key === 'ArrowRight') {
      this.onNextDocument();
    } else if (event.ctrlKey && event.key === 'o') {
      this.onViewDocumentInNewTab();
    }
  }

  onRotate() {
    const angles = [0, 90, 180, 270];
    const currentIndex = angles.indexOf(this.documentAngle());
    this.documentAngle.set(angles[(currentIndex + 1) % angles.length] as 0 | 90 | 180 | 270);
  }

  onZoomOut() {
    let zLevel = this.zoomLevel() as number;

    if (zLevel > this.zoomMin) {
      zLevel -= 20;
      this.zoomLevel.set(zLevel);
    }
  }

  onZoomIn() {
    let zLevel = this.zoomLevel() as number;

    if (zLevel < this.zoomMax) {
      zLevel += 20;
      this.zoomLevel.set(zLevel);
    }
  }

  onNextDocument() {
    this.clearViewer();
    this.nextDocument.emit();
  }

  onPreviousDocument() {
    this.clearViewer();
    this.previousDocument.emit();
  }
}
